import { useEffect, useState } from 'react';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { AdminEmpty, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';

type SponsorRow = {
  id: string;
  konan_id: string | null;
  email: string;
  first_name: string;
  last_name: string;
  referral_count: number;
};

function currentQuarterKey(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

export function AdminReferralsPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<SponsorRow[]>([]);
  const [quarter, setQuarter] = useState(currentQuarterKey());
  const [amount, setAmount] = useState('25');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(true);

  async function load() {
    setBusy(true);
    const { data: profiles, error: err } = await kuunda
      .from('profiles')
      .select('id, konan_id, email, first_name, last_name, referral_cycle_started_at')
      .eq('role', 'user')
      .order('created_at', { ascending: false })
      .limit(300);
    if (err) {
      toast.error(err.message);
      setBusy(false);
      return;
    }

    const sponsors = profiles ?? [];
    const sponsorIds = sponsors.map((p) => p.id);
    if (sponsorIds.length === 0) {
      setRows([]);
      setBusy(false);
      return;
    }

    const { data: referred, error: refErr } = await kuunda
      .from('profiles')
      .select('id, referred_by, created_at')
      .in('referred_by', sponsorIds);
    if (refErr) {
      toast.error(refErr.message);
      setBusy(false);
      return;
    }

    const cycleBySponsor = Object.fromEntries(
      sponsors.map((p) => [p.id, p.referral_cycle_started_at ?? '1970-01-01']),
    );
    const counts = new Map<string, number>();
    for (const r of referred ?? []) {
      if (!r.referred_by) continue;
      const cycleStart = cycleBySponsor[r.referred_by] ?? '1970-01-01';
      if ((r.created_at ?? '') < cycleStart) continue;
      counts.set(r.referred_by, (counts.get(r.referred_by) ?? 0) + 1);
    }

    const list: SponsorRow[] = sponsors
      .map((p) => ({
        id: p.id,
        konan_id: p.konan_id,
        email: p.email,
        first_name: p.first_name,
        last_name: p.last_name,
        referral_count: counts.get(p.id) ?? 0,
      }))
      .filter((r) => r.referral_count > 0)
      .sort((a, b) => b.referral_count - a.referral_count);

    setRows(list);
    setBusy(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function grantVoucher(userId: string, referralCount: number) {
    if (!profile) return;
    setLoading(true);
    const code = `ISBD-${quarter}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const { error: err } = await kuunda.from('referral_vouchers').upsert(
      {
        user_id: userId,
        quarter_key: quarter,
        referral_count: referralCount,
        voucher_code: code,
        voucher_amount: Number(amount) || 0,
        currency: 'XOF',
        granted_by: profile.id,
      },
      { onConflict: 'user_id,quarter_key' },
    );
    setLoading(false);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success(`Bon octroyé : ${code}`, 'Parrainage');
    }
  }

  async function purgeInactive() {
    if (!window.confirm('Désactiver les comptes inactifs depuis 12 mois ?')) return;
    const { data, error: err } = await kuunda.rpc('purge_inactive_accounts');
    if (err) {
      toast.error(err.message);
    } else {
      const n = typeof data === 'number' ? data : Number(data) || 0;
      toast.info(`${n} compte(s) traité(s).`, 'Purge');
    }
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Parrainage"
        description="Octroi des bons d’achat selon les filleuls du cycle en cours."
      />

      <section className="admin-panel">
        <div className="admin-toolbar">
          <div className="field">
            <label>Trimestre</label>
            <input value={quarter} onChange={(e) => setQuarter(e.target.value)} />
          </div>
          <div className="field">
            <label>Montant bon (€)</label>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            Actualiser
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => void purgeInactive()}>
            Purger inactifs 12 mois
          </button>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <h3>Parrains du cycle ({rows.length})</h3>
        </div>
        {busy ? <ListSkeleton rows={4} /> : null}
        {!busy && rows.length === 0 ? (
          <AdminEmpty icon="gift" title="Aucun parrainage" hint="Aucun filleul sur le cycle courant." />
        ) : (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {rows.map((r) => (
              <article
                key={r.id}
                className="admin-list-item"
                style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <strong>{r.konan_id ?? r.id.slice(0, 8)}</strong>
                  <div className="muted" style={{ fontSize: '0.85rem' }}>
                    {[r.first_name, r.last_name].filter(Boolean).join(' ') || r.email} · {r.referral_count}{' '}
                    filleul(s)
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={loading}
                  onClick={() => void grantVoucher(r.id, r.referral_count)}
                >
                  Octroyer un bon
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
